<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-pill me-2"></i>{{ t('pharm.title','Pharmacy') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button v-if="tab === 'stock'" class="hms-btn hms-btn-blue" @click="openAlerts">
            <i class="mdi mdi-bell-outline me-1"></i>Low-stock Alerts
          </button>
          <button v-if="tab === 'stock'" class="hms-btn hms-btn-primary" @click="openAddStock">
            <i class="mdi mdi-package-variant-plus me-1"></i>Add Stock
          </button>
        </div>
      </div>

      <!-- Stat row -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="setStockFilter('all')" :class="{ selected: stockFilter === 'all' && tab === 'stock' }">
          <div class="hms-stat-num">{{ stock.length }}</div>
          <div class="hms-stat-lbl">Stock Items</div>
        </div>
        <div class="hms-stat" @click="setStockFilter('low')" :class="{ selected: stockFilter === 'low' && tab === 'stock' }">
          <div class="hms-stat-num hms-stat-red">{{ lowStockCount }}</div>
          <div class="hms-stat-lbl">Low Stock</div>
        </div>
        <div class="hms-stat" @click="setStockFilter('expiring')" :class="{ selected: stockFilter === 'expiring' && tab === 'stock' }">
          <div class="hms-stat-num hms-stat-amber">{{ expiringCount }}</div>
          <div class="hms-stat-lbl">Expiring &lt; 90d</div>
        </div>
        <div class="hms-stat" @click="setStockFilter('out')" :class="{ selected: stockFilter === 'out' && tab === 'stock' }">
          <div class="hms-stat-num hms-stat-info">{{ outOfStockCount }}</div>
          <div class="hms-stat-lbl">Out of Stock</div>
        </div>
        <div class="hms-stat" @click="goRxTab('pending')">
          <div class="hms-stat-num hms-stat-purple">{{ pendingRxCount }}</div>
          <div class="hms-stat-lbl">Pending Rx</div>
        </div>
        <div class="hms-stat" @click="goRxTab('partially_dispensed')">
          <div class="hms-stat-num hms-stat-info">{{ partialRxCount }}</div>
          <div class="hms-stat-lbl">Partial Rx</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab === 'stock' }" @click="tab = 'stock'">
          <i class="mdi mdi-package-variant-closed me-1"></i>Stock
        </button>
        <button class="hms-tab" :class="{ active: tab === 'prescriptions' }" @click="tab = 'prescriptions'">
          <i class="mdi mdi-prescription me-1"></i>Prescriptions
          <span class="hms-tab-count">{{ pendingRxCount + partialRxCount }}</span>
        </button>
        <button class="hms-tab" :class="{ active: tab === 'queue' }" @click="tab = 'queue'; loadDrugQueue()">
          <i class="mdi mdi-account-arrow-right me-1"></i>Dispense Queue
          <span v-if="drugQueue.length" class="hms-tab-count">{{ drugQueue.length }}</span>
        </button>
      </div>

      <!-- Sharon F#3 #14: drug-level dispense queue, one line per drug item ready to dispense -->
      <div v-if="tab === 'queue'">
        <div class="hms-card">
          <div class="d-flex justify-content-between mb-2">
            <h6 class="m-0"><i class="mdi mdi-account-arrow-right"></i> Patients queued for dispense — {{ groupedQueue.length }} patient{{ groupedQueue.length === 1 ? '' : 's' }} · {{ drugQueue.length }} drug items</h6>
            <button class="hms-btn hms-btn-sm" @click="loadDrugQueue" :disabled="loadingQueue">
              <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loadingQueue }"></i> Refresh
            </button>
          </div>

          <!-- Grouped by patient/prescription (HMIS_Issues: dispense grouped per patient) -->
          <div v-for="g in groupedQueue" :key="g.prescription_id" class="hms-rx-group">
            <div class="hms-rx-group-head">
              <div>
                <span class="fw-bold hms-text">{{ g.patient_name }}</span>
                <span class="hms-muted-sm ms-2">{{ g.patient_no }} · {{ g.prescription_number }} · Dr: {{ g.doctor_name || '—' }}</span>
              </div>
              <button class="hms-btn hms-btn-sm hms-btn-primary" @click="openRxFromQueue(g.prescription_id)">
                <i class="mdi mdi-pill me-1"></i>Dispense {{ g.items.length }} item{{ g.items.length === 1 ? '' : 's' }}
              </button>
            </div>
            <table class="hms-table">
              <thead>
                <tr><th>Drug</th><th>Dose</th><th>Freq</th><th>Duration</th><th>Qty</th><th>Status</th></tr>
              </thead>
              <tbody>
                <tr v-for="li in g.items" :key="li.item_id">
                  <td class="fw-bold">{{ li.drug_name }}</td>
                  <td>{{ li.dosage || '—' }}</td>
                  <td>{{ li.frequency || '—' }}</td>
                  <td>{{ li.duration || '—' }}</td>
                  <td>{{ li.quantity_prescribed }} <span v-if="li.quantity_dispensed > 0" class="hms-muted-sm">({{ li.quantity_dispensed }} given)</span></td>
                  <td><span class="hms-badge" :class="li.status==='pending'?'hms-badge-warning':'hms-badge-info'">{{ li.status }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="!loadingQueue && drugQueue.length === 0" class="hms-empty">
            <i class="mdi mdi-pill"></i>Queue is empty — all prescriptions have been dispensed.
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="hms-spinner">
        <div class="spinner-border"></div>
      </div>

      <!-- ============ STOCK TAB ============ -->
      <div v-else-if="tab === 'stock'">
        <div class="hms-card">
          <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="stockSearch" placeholder="Search drug name, generic, batch..." />
            </div>
            <select class="hms-input hms-filter" v-model="stockFilter">
              <option value="all">All stock</option>
              <option value="low">Low stock (≤ reorder)</option>
              <option value="expiring">Expiring &lt; 90 days</option>
              <option value="out">Out of stock</option>
              <option value="expired">Expired</option>
              <option value="controlled">Controlled / narcotics</option>
            </select>
            <span class="hms-muted ms-auto">{{ filteredStock.length }} items</span>
          </div>

          <div v-if="filteredStock.length" style="overflow-x:auto;">
            <table class="hms-table">
              <thead>
                <tr>
                  <th>Drug</th>
                  <th>Form / Strength</th>
                  <th>Stock</th>
                  <th>Reorder</th>
                  <th>Unit Price</th>
                  <th>Expiry</th>
                  <th>Batch</th>
                  <th>Level</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in filteredStock" :key="s.stock_id">
                  <td>
                    <div class="hms-text fw-semibold">
                      {{ s.drug_name }}
                      <i v-if="s.is_narcotic == 1" class="mdi mdi-needle text-danger ms-1" title="Narcotic"></i>
                      <i v-else-if="s.is_controlled == 1" class="mdi mdi-shield-lock text-warning ms-1" title="Controlled"></i>
                    </div>
                    <div class="hms-muted-sm">{{ s.generic_name || '—' }} · {{ s.category || '' }}</div>
                  </td>
                  <td>
                    <div>{{ s.dosage_form || '—' }}</div>
                    <div class="hms-muted-sm">{{ s.strength || '' }}</div>
                  </td>
                  <td>
                    <div :class="stockNumClass(s)">{{ formatQty(s.quantity_in_stock) }} {{ s.unit }}</div>
                  </td>
                  <td>{{ formatQty(s.reorder_level) }}</td>
                  <td>KES {{ formatMoney(s.unit_price) }}</td>
                  <td>
                    <span :class="expiryClass(s.expiry_date)">{{ formatDate(s.expiry_date) }}</span>
                    <div class="hms-muted-sm" v-if="s.expiry_date">{{ daysToExpiry(s.expiry_date) }}d</div>
                  </td>
                  <td><span class="hms-mono">{{ s.batch_number || '—' }}</span></td>
                  <td>
                    <span class="hms-badge" :class="levelBadge(s)">{{ levelLabel(s) }}</span>
                  </td>
                  <td>
                    <span class="hms-badge" :class="expiryStatusBadge(s)">{{ expiryStatusLabel(s) }}</span>
                  </td>
                  <td class="text-nowrap">
                    <button class="hms-btn hms-btn-sm hms-btn-blue me-1" @click="openAddBatch(s)" title="Add new batch of this drug">
                      <i class="mdi mdi-plus-box-multiple"></i>
                    </button>
                    <button class="hms-btn hms-btn-sm hms-btn-amber me-1" @click="openEditStock(s)" title="Edit">
                      <i class="mdi mdi-pencil"></i>
                    </button>
                    <button class="hms-btn hms-btn-sm hms-btn-purple me-1" @click="openAdjust(s)" title="Adjust quantity (damaged / expired / count correction)">
                      <i class="mdi mdi-scale-balance"></i>
                    </button>
                    <button v-if="parseFloat(s.quantity_in_stock) <= 0" class="hms-btn hms-btn-sm hms-btn-danger" @click="removeStockRow(s)" title="Remove this empty stock row">
                      <i class="mdi mdi-trash-can-outline"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty">
            <i class="mdi mdi-package-variant-closed-remove"></i>
            <div v-if="stockSearch || stockFilter !== 'all'">No stock matches the current filter.</div>
            <div v-else>No stock yet — click <strong>Add Stock</strong> to begin.</div>
          </div>
        </div>
      </div>

      <!-- ============ PRESCRIPTIONS TAB ============ -->
      <div v-else-if="tab === 'prescriptions'">
        <div class="hms-card">
          <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="rxSearch" placeholder="Search patient name, Rx number..." />
            </div>
            <select class="hms-input hms-filter" v-model="rxFilter">
              <option value="pending">Pending</option>
              <option value="partially_dispensed">Partially dispensed</option>
              <option value="fully_dispensed">Fully dispensed</option>
              <option value="cancelled">Cancelled</option>
              <option value="">All</option>
            </select>
            <span class="hms-muted ms-auto">{{ filteredRx.length }} prescriptions</span>
          </div>

          <div v-if="filteredRx.length" style="overflow-x:auto;">
            <table class="hms-table">
              <thead>
                <tr>
                  <th>Rx Number</th>
                  <th>Patient</th>
                  <th>Doctor</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <!-- One row per patient per day; multi-Rx days expand on click -->
                <template v-for="g in groupedRx" :key="g.key">
                  <tr v-if="g.rxs.length > 1" class="rx-group-row" @click="toggleRxGroup(g.key)">
                    <td>
                      <i class="mdi" :class="expandedRxGroups[g.key] ? 'mdi-chevron-down' : 'mdi-chevron-right'"></i>
                      <span class="hms-badge hms-badge-info ms-1">{{ g.rxs.length }} prescriptions</span>
                    </td>
                    <td>
                      <div class="hms-text fw-semibold">{{ g.patient_name }}</div>
                      <div class="hms-muted-sm">{{ g.patient_no }}</div>
                    </td>
                    <td>{{ groupDoctors(g) }}</td>
                    <td>{{ formatDate(g.day) }}</td>
                    <td>
                      <span v-for="s in groupStatusCounts(g)" :key="s.status" class="hms-badge me-1" :class="rxStatusBadge(s.status)">
                        {{ s.count }} {{ rxStatusLabel(s.status) }}
                      </span>
                    </td>
                    <td class="hms-muted-sm">
                      <i class="mdi mdi-gesture-tap"></i> Click to {{ expandedRxGroups[g.key] ? 'collapse' : 'view all' }}
                    </td>
                  </tr>
                  <template v-for="rx in g.rxs" :key="rx.prescription_id">
                    <tr v-if="g.rxs.length === 1 || expandedRxGroups[g.key]" :class="{ 'rx-sub-row': g.rxs.length > 1 }">
                      <td>
                        <span v-if="g.rxs.length > 1" class="rx-sub-indent"></span>
                        <span class="hms-mono">{{ rx.prescription_number }}</span>
                      </td>
                      <td>
                        <template v-if="g.rxs.length === 1">
                          <div class="hms-text fw-semibold">{{ rx.patient_name }}</div>
                          <div class="hms-muted-sm">{{ rx.patient_no }}</div>
                        </template>
                        <span v-else class="hms-muted-sm">↳ {{ rx.patient_name }}</span>
                      </td>
                      <td>{{ rx.doctor_name || '—' }}</td>
                      <td>{{ formatDateTime(rx.prescription_date) }}</td>
                      <td>
                        <span class="hms-badge" :class="rxStatusBadge(rx.status)">{{ rxStatusLabel(rx.status) }}</span>
                      </td>
                      <td>
                        <button v-if="rx.status === 'pending' || rx.status === 'partially_dispensed'"
                                class="hms-btn hms-btn-sm hms-btn-primary me-1"
                                @click="openDispense(rx)" title="Dispense">
                          <i class="mdi mdi-pill me-1"></i>Dispense
                        </button>
                        <button class="hms-btn hms-btn-sm hms-btn-blue me-1" @click="openViewRx(rx)" title="View">
                          <i class="mdi mdi-eye"></i>
                        </button>
                        <button class="hms-btn hms-btn-sm hms-btn-blue me-1" @click="printRx(rx.prescription_id)" title="Print prescription">
                          <i class="mdi mdi-printer"></i>
                        </button>
                        <button v-if="rx.status === 'pending'"
                                class="hms-btn hms-btn-sm hms-btn-red"
                                @click="confirmCancel(rx)" title="Cancel">
                          <i class="mdi mdi-close"></i>
                        </button>
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty">
            <i class="mdi mdi-prescription"></i>No prescriptions match.
          </div>
        </div>
      </div>

      <!-- ============ ADD/EDIT STOCK MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showStockForm" @click.self="showStockForm=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi" :class="editingStock ? 'mdi-pencil' : 'mdi-package-variant-plus'"></i> {{ editingStock ? 'Edit Stock' : (batchMode ? 'Add Batch — ' + stockForm.drug_name : 'Add Stock') }}</span>
            <button class="hms-modal-close" @click="showStockForm=false">&times;</button>
          </div>
          <div v-if="stockError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ stockError }}</div>

          <div class="hms-section-label mb-2">DRUG</div>
          <div class="row g-3 mb-3">
            <div class="col-md-7"><label class="hms-label">Drug Name *</label>
              <input class="hms-input" v-model="stockForm.drug_name" placeholder="e.g. Amoxicillin 500mg" />
            </div>
            <div class="col-md-5"><label class="hms-label">Generic Name</label>
              <input class="hms-input" v-model="stockForm.generic_name" placeholder="Amoxicillin" />
            </div>
            <div class="col-md-5"><label class="hms-label">Category</label>
              <input class="hms-input" v-model="stockForm.category" placeholder="e.g. Antibiotic" />
            </div>
            <div class="col-md-4"><label class="hms-label">Dosage Form</label>
              <select class="hms-input" v-model="stockForm.dosage_form">
                <option value="">Select...</option>
                <option value="Tablet">Tablet</option>
                <option value="Capsule">Capsule</option>
                <option value="Suspension">Suspension</option>
                <option value="Syrup">Syrup</option>
                <option value="Injection">Injection</option>
                <option value="IV vial">IV vial</option>
                <option value="Inhaler">Inhaler</option>
                <option value="Cream">Cream</option>
                <option value="Drops">Drops</option>
                <option value="Pen">Pen</option>
                <option value="Patch">Patch</option>
              </select>
            </div>
            <div class="col-md-3"><label class="hms-label">Strength</label>
              <input class="hms-input" v-model="stockForm.strength" placeholder="500mg" />
            </div>
          </div>

          <div class="hms-section-label">QUANTITY &amp; PRICING</div>
          <div class="row g-3 mb-3">
            <div class="col-md-3"><label class="hms-label">{{ editingStock ? 'Reorder Level' : 'Quantity in Stock' }} *</label>
              <input v-if="!editingStock" type="number" class="hms-input" v-model.number="stockForm.quantity_in_stock" min="0" step="0.01" />
              <input v-else type="number" class="hms-input" v-model.number="stockForm.reorder_level" min="0" step="0.01" />
            </div>
            <div class="col-md-3" v-if="!editingStock"><label class="hms-label">Unit (caps/tabs/ml)</label>
              <input class="hms-input" v-model="stockForm.unit" placeholder="caps" />
            </div>
            <div class="col-md-3" v-if="!editingStock"><label class="hms-label">Reorder Level</label>
              <input type="number" class="hms-input" v-model.number="stockForm.reorder_level" min="0" step="0.01" />
            </div>
            <div class="col-md-3"><label class="hms-label">Unit Cost (KES)</label>
              <input type="number" class="hms-input" v-model.number="stockForm.unit_cost" min="0" step="0.01" />
            </div>
            <div class="col-md-3"><label class="hms-label">Unit Price (KES)</label>
              <input type="number" class="hms-input" v-model.number="stockForm.unit_price" min="0" step="0.01" />
            </div>
          </div>

          <div class="hms-section-label">BATCH &amp; SUPPLIER</div>
          <div class="row g-3 mb-3">
            <div class="col-md-3"><label class="hms-label">Batch Number</label>
              <input class="hms-input" v-model="stockForm.batch_number" placeholder="BCH-2026-04" />
            </div>
            <div class="col-md-3"><label class="hms-label">Expiry Date</label>
              <input type="date" class="hms-input" v-model="stockForm.expiry_date" />
            </div>
            <div class="col-md-3"><label class="hms-label">Supplier</label>
              <select class="hms-input" v-model="stockForm.supplier">
                <option value="">Select supplier...</option>
                <option v-for="sp in supplierOptions" :key="sp" :value="sp">{{ sp }}</option>
              </select>
              <div class="hms-muted-sm mt-1" v-if="!suppliers.length">No registered suppliers — add them under Stores &rarr; Suppliers.</div>
            </div>
            <div class="col-md-3"><label class="hms-label">Receiving Store</label>
              <input class="hms-input" v-model="stockForm.receiving_store" list="hmsStoreOptions" placeholder="Main Pharmacy Store" />
              <datalist id="hmsStoreOptions">
                <option v-for="st in storeOptions" :key="st" :value="st"></option>
              </datalist>
            </div>
          </div>

          <div class="d-flex flex-wrap gap-3 mb-3">
            <label class="hms-check"><input type="checkbox" v-model="stockForm.is_controlled" :true-value="1" :false-value="0" /> Controlled drug</label>
            <label class="hms-check" v-if="stockForm.is_controlled == 1"><input type="checkbox" v-model="stockForm.is_narcotic" :true-value="1" :false-value="0" /> Narcotic</label>
          </div>

          <div v-if="editingStock" class="mb-3">
            <label class="hms-label">Status</label>
            <select class="hms-input" v-model="stockForm.status">
              <option value="active">Active</option>
              <option value="recalled">Recalled</option>
              <option value="out_of_stock">Out of stock</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          <button class="hms-btn hms-btn-primary w-100 mt-2" @click="saveStock" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : (editingStock ? 'Save Changes' : 'Add Stock') }}
          </button>
        </div>
      </div>

      <!-- ============ STOCK ADJUSTMENT MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showAdjust" @click.self="showAdjust=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-scale-balance"></i> Adjust Stock — {{ adjustForm.drug_name }}</span>
            <button class="hms-modal-close" @click="showAdjust=false">&times;</button>
          </div>
          <div v-if="adjustError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ adjustError }}</div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Current quantity</span><span class="hms-mono">{{ formatQty(adjustForm.current_qty) }} {{ adjustForm.unit }}</span></div>
            <div v-if="adjustForm.batch_number"><span class="hms-kv-key">Batch</span><span class="hms-mono">{{ adjustForm.batch_number }}</span></div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">New Quantity on Hand *</label>
              <input type="number" class="hms-input" v-model.number="adjustForm.new_quantity" min="0" step="0.01" />
              <div class="hms-muted-sm mt-1" v-if="adjustDelta !== 0">
                {{ adjustDelta > 0 ? '+' : '' }}{{ formatQty(adjustDelta) }} {{ adjustForm.unit }}
              </div>
            </div>
            <div class="col-md-6"><label class="hms-label">Reason *</label>
              <select class="hms-input" v-model="adjustForm.reason">
                <option value="">Select reason...</option>
                <option value="damaged">Damaged</option>
                <option value="expired">Expired</option>
                <option value="count_correction">Stock count correction</option>
                <option value="return">Return to supplier</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Notes</label>
              <textarea class="hms-input" rows="2" v-model="adjustForm.notes" placeholder="Optional details, e.g. water-damaged carton"></textarea>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="saveAdjust" :disabled="adjustBusy">
            <span v-if="adjustBusy" class="spinner-border spinner-border-sm me-1"></span>
            {{ adjustBusy ? 'Saving...' : 'Save Adjustment' }}
          </button>
        </div>
      </div>

      <!-- ============ DISPENSE MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showDispense" @click.self="closeDispense">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-pill me-2"></i>Dispense Prescription</span>
            <button class="hms-modal-close" @click="closeDispense">&times;</button>
          </div>
          <div v-if="dispenseError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ dispenseError }}</div>

          <div v-if="dispenseLoading" class="hms-spinner py-3"><div class="spinner-border"></div></div>

          <div v-else-if="rxDetail">
            <UnpaidBanner :patient-id="rxDetail.patient_id" />
            <div class="hms-kv mb-3">
              <div><span class="hms-kv-key">Rx Number</span><span class="hms-mono">{{ rxDetail.prescription_number }}</span></div>
              <div><span class="hms-kv-key">Patient</span><span>{{ rxDetail.patient_name }} ({{ rxDetail.patient_no }})</span></div>
              <div><span class="hms-kv-key">Doctor</span><span>{{ rxDetail.doctor_name || '—' }}</span></div>
              <div v-if="rxDetail.notes"><span class="hms-kv-key">Notes</span><span>{{ rxDetail.notes }}</span></div>
            </div>

            <div class="hms-section-label">DRUGS</div>
            <div v-for="line in rxItems" :key="line.item_id" class="hms-rx-line">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <div class="hms-text fw-semibold">{{ line.drug_name }}</div>
                  <div class="hms-muted-sm">
                    {{ line.dosage }} · {{ line.frequency }} · {{ line.duration }} · {{ line.route }}
                  </div>
                </div>
                <span class="hms-badge" :class="rxItemBadge(line.status)">{{ rxItemLabel(line.status) }}</span>
              </div>
              <div class="row g-2 align-items-end">
                <div class="col-sm-3">
                  <label class="hms-label">Prescribed</label>
                  <input class="hms-input" :value="line.quantity_prescribed" disabled />
                </div>
                <div class="col-sm-3">
                  <label class="hms-label">Already disp.</label>
                  <input class="hms-input" :value="line.quantity_dispensed || 0" disabled />
                </div>
                <div class="col-sm-3">
                  <label class="hms-label">In Stock</label>
                  <div :class="stockAvailClass(line)">{{ line.stock_available !== null ? line.stock_available : 'N/A' }}</div>
                </div>
                <div class="col-sm-3">
                  <label class="hms-label">Dispense Now</label>
                  <input
                    type="number"
                    class="hms-input"
                    v-model.number="line.dispense_now"
                    :disabled="line.status === 'dispensed' || line.status === 'cancelled'"
                    :max="maxDispense(line)"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div v-if="!line.stock_id" class="col-12">
                  <label class="hms-label">Pick Stock</label>
                  <select class="hms-input" v-model="line.stock_id" @change="onStockPicked(line)">
                    <option value="">Match to stock...</option>
                    <option v-for="s in stockSuggestions(line)" :key="s.stock_id" :value="s.stock_id">
                      {{ s.drug_name }} ({{ s.quantity_in_stock }} {{ s.unit }} avail) — KES {{ formatMoney(s.unit_price) }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-center mt-3">
              <SaveAndQueue ref="sqDispense" from-point="pharmacy" :saving="processing" :disabled="!canDispense"
                            save-label="Dispense Selected Items" saving-label="Dispensing..."
                            btn-class="hms-btn hms-btn-primary" icon="mdi-pill" @save="doDispense" />
            </div>
          </div>
        </div>
      </div>

      <!-- ============ VIEW RX MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showViewRx" @click.self="showViewRx=false">
        <div class="hms-modal" v-if="rxDetail">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-prescription me-2"></i>{{ rxDetail.prescription_number }}</span>
            <button class="hms-modal-close" @click="showViewRx=false">&times;</button>
          </div>
          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Patient</span><span>{{ rxDetail.patient_name }} ({{ rxDetail.patient_no }})</span></div>
            <div><span class="hms-kv-key">Doctor</span><span>{{ rxDetail.doctor_name || '—' }}</span></div>
            <div><span class="hms-kv-key">Date</span><span>{{ formatDateTime(rxDetail.prescription_date) }}</span></div>
            <div><span class="hms-kv-key">Status</span><span><span class="hms-badge" :class="rxStatusBadge(rxDetail.status)">{{ rxStatusLabel(rxDetail.status) }}</span></span></div>
            <div v-if="rxDetail.notes"><span class="hms-kv-key">Notes</span><span>{{ rxDetail.notes }}</span></div>
          </div>
          <div class="hms-section-label">DRUGS</div>
          <table class="hms-table">
            <thead><tr><th>Drug</th><th>Dosage</th><th>Qty Rx</th><th>Disp.</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="line in rxItems" :key="line.item_id">
                <td>{{ line.drug_name }}</td>
                <td class="hms-muted-sm">{{ line.dosage }} · {{ line.frequency }} · {{ line.duration }}</td>
                <td>{{ line.quantity_prescribed }}</td>
                <td>{{ line.quantity_dispensed }}</td>
                <td><span class="hms-badge" :class="rxItemBadge(line.status)">{{ rxItemLabel(line.status) }}</span></td>
              </tr>
            </tbody>
          </table>
          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-blue ms-auto" @click="printRx(rxDetail.prescription_id)">
              <i class="mdi mdi-printer me-1"></i>Print Prescription
            </button>
          </div>
        </div>
      </div>

      <!-- ============ LOW-STOCK ALERTS MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showAlerts" @click.self="showAlerts=false">
        <div class="hms-modal" style="max-width:540px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-bell-outline me-2"></i>Low-stock Email Alerts</span>
            <button class="hms-modal-close" @click="showAlerts=false">&times;</button>
          </div>
          <p class="hms-muted">When any drug drops to or below its reorder level, an email listing the affected items is sent to the addresses below. A cooldown prevents repeat emails for the same episode.</p>
          <div v-if="alertError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ alertError }}</div>

          <table class="hms-table mb-3" v-if="alertRules.length">
            <thead><tr><th>Recipient(s)</th><th>Cooldown</th><th>Last sent</th><th></th></tr></thead>
            <tbody>
              <tr v-for="r in alertRules" :key="r.rule_id">
                <td>{{ r.email_to }}</td>
                <td>{{ r.cooldown_hours }}h</td>
                <td class="hms-muted-sm">{{ r.last_fired_at ? formatDateTime(r.last_fired_at) : 'Never' }}</td>
                <td><button class="hms-btn hms-btn-sm hms-btn-red" @click="removeAlertRule(r)" title="Remove"><i class="mdi mdi-close"></i></button></td>
              </tr>
            </tbody>
          </table>
          <div v-else class="hms-muted mb-3"><i class="mdi mdi-information-outline me-1"></i>No alert recipients configured yet — add one below to activate low-stock emails.</div>

          <div class="hms-section-label">ADD RECIPIENT</div>
          <div class="row g-2 align-items-end">
            <div class="col-7"><label class="hms-label">Email (comma-separate for multiple)</label>
              <input class="hms-input" v-model="alertEmail" placeholder="pharmacy@hospital.co.ke" />
            </div>
            <div class="col-2"><label class="hms-label">Cooldown (h)</label>
              <input type="number" class="hms-input" v-model.number="alertCooldown" min="1" />
            </div>
            <div class="col-3">
              <button class="hms-btn hms-btn-primary w-100" @click="addAlertRule" :disabled="alertBusy">
                <span v-if="alertBusy" class="spinner-border spinner-border-sm me-1"></span>Add
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Cancel confirm -->
      <div class="hms-modal-overlay" v-if="cancelingRx" @click.self="cancelingRx=null">
        <div class="hms-modal" style="max-width:420px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-alert-octagram me-2 text-danger"></i>Cancel Prescription</span>
            <button class="hms-modal-close" @click="cancelingRx=null">&times;</button>
          </div>
          <p class="hms-card-text">Cancel <strong>{{ cancelingRx.prescription_number }}</strong> for {{ cancelingRx.patient_name }}?</p>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-blue flex-fill" @click="cancelingRx=null">No, keep</button>
            <button class="hms-btn hms-btn-red flex-fill" @click="doCancel" :disabled="processing">
              <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>Yes, cancel
            </button>
          </div>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body {
  background: var(--hms-bg2) !important; color: var(--hms-h) !important;
}
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; color: var(--hms-h); }

/* Stat row */
.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 150px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-amber  { color: #f1b44c; }
.hms-stat-red    { color: var(--hms-danger); }
.hms-stat-info   { color: var(--hms-primary); }
.hms-stat-purple { color: #9b59b6; }

/* Tabs */
.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }
.hms-tab-count { background: var(--hms-primary); color: #fff; border-radius: 999px; padding: 1px 7px; font-size: 10px; margin-left: 8px; }

/* Card */
.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }

/* Spinner / empty */
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

/* Search */
.hms-search-wrap { position: relative; flex: 1; min-width: 240px; max-width: 360px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }

/* Inputs */
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-input:disabled { background: var(--hms-bg3); color: var(--hms-s); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 8px; }
.hms-check { display: inline-flex; align-items: center; gap: 6px; color: var(--hms-h); font-size: 13px; cursor: pointer; }
.hms-check input { accent-color: var(--hms-primary); }

/* Table */
.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }

/* Stock numbers */
.stock-low      { color: var(--hms-danger); font-weight: 700; }
.stock-out      { color: var(--hms-danger); font-weight: 800; }
.stock-ok       { color: var(--hms-success); font-weight: 600; }

/* Expiry colour */
.expiry-soon    { color: #f1b44c; font-weight: 600; }
.expiry-critical{ color: var(--hms-danger); font-weight: 700; }
.expiry-ok      { color: var(--hms-h); }

/* Badges */
.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }

/* Buttons */
.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue   { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber  { background: rgba(245,158,11,.15);  color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-red    { background: rgba(220,53,69,.15);   color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-red:hover:not(:disabled) { background: rgba(220,53,69,.25); }
.hms-btn-purple { background: rgba(139,92,246,.15); color: #8b5cf6; border: 1px solid rgba(139,92,246,.3); }
.hms-btn-purple:hover:not(:disabled) { background: rgba(139,92,246,.25); }
.hms-btn-danger { background: rgba(239,68,68,.15); color: #ef4444; border: 1px solid rgba(239,68,68,.3); }
.hms-btn-danger:hover:not(:disabled) { background: rgba(239,68,68,.25); }

/* Modal */
.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 760px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

/* KV */
.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 140px; font-weight: 600; }

/* Rx line */
.hms-rx-line { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 14px; margin-bottom: 10px; }

/* Grouped prescriptions (one row per patient per day) */
.rx-group-row { cursor: pointer; background: var(--hms-bg2); }
.rx-group-row:hover td { background: rgba(44, 95, 209, 0.06); }
.rx-sub-row td { background: rgba(44, 95, 209, 0.03); }
.rx-sub-indent { display: inline-block; width: 18px; }

/* Grouped dispense queue */
.hms-rx-group { border: 1px solid var(--hms-br); border-radius: 12px; margin-bottom: 14px; overflow: hidden; }
.hms-rx-group-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; padding: 10px 14px; background: var(--hms-bg2); border-bottom: 1px solid var(--hms-br); }

/* Alerts */
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-card-text { color: var(--hms-m); font-size: 14px; line-height: 1.6; margin: 12px 0 18px; }

/* Toast */
.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }

@media (max-width: 768px) {
  .hms-stat-row .hms-stat { min-width: calc(50% - 6px); }
  .hms-modal { padding: 18px; }
}
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import SaveAndQueue from "@/components/hms-shared/SaveAndQueue.vue";
import UnpaidBanner from "@/components/hms-shared/UnpaidBanner.vue";
import { executeJson, hms } from "@/api";
import { useI18n } from "@/api/i18n";

export default {
  name: "PharmacyManagement",
  components: { Navigation, SaveAndQueue, UnpaidBanner },
  data() {
    return {
      loading: true,
      processing: false,
      tab: "stock",
      drugQueue: [],
      loadingQueue: false,
      i18n: useI18n(),

      stock: [],
      stockSearch: "",
      stockFilter: "all",

      prescriptions: [],
      rxSearch: "",
      rxFilter: "pending",
      expandedRxGroups: {},

      // Stock form
      showStockForm: false,
      editingStock:  false,
      batchMode:     false,
      stockError:    "",
      stockForm:     this.emptyStockForm(),

      // Registered suppliers (Stores module) for the supplier dropdown
      suppliers: [],
      hospStores: [],

      // Stock adjustment
      showAdjust:  false,
      adjustBusy:  false,
      adjustError: "",
      adjustForm:  { stock_id: null, drug_name: "", unit: "", batch_number: "", current_qty: 0, new_quantity: 0, reason: "", notes: "" },

      // Low-stock alert rules
      showAlerts:    false,
      alertRules:    [],
      alertEmail:    "",
      alertCooldown: 24,
      alertBusy:     false,
      alertError:    "",

      // Dispense
      showDispense:    false,
      dispenseLoading: false,
      dispenseError:   "",
      rxDetail:        null,
      rxItems:         [],

      // View Rx
      showViewRx: false,

      // Cancel
      cancelingRx: null,

      // Toast
      toastMsg: "",
      toastError: false,
      toastTimer: null,

      branchId:   sessionStorage.getItem("branchId"),
      hospitalId: sessionStorage.getItem("hospitalId")
    };
  },
  computed: {
    supplierOptions() {
      var names = this.suppliers.map(s => s.name).filter(Boolean);
      // Keep a legacy free-text supplier visible when editing an old row
      if (this.stockForm.supplier && names.indexOf(this.stockForm.supplier) === -1) names.unshift(this.stockForm.supplier);
      return names;
    },
    storeOptions() {
      var opts = this.hospStores.map(s => s.store_name).filter(Boolean);
      if (!opts.length) opts = ["Main Pharmacy Store", "Main Store", "OPD Pharmacy", "IPD Pharmacy", "Emergency Store"];
      this.stock.forEach(s => { if (s.receiving_store && opts.indexOf(s.receiving_store) === -1) opts.push(s.receiving_store); });
      return opts;
    },
    adjustDelta() { return (Number(this.adjustForm.new_quantity) || 0) - (Number(this.adjustForm.current_qty) || 0); },
    lowStockCount()    { return this.stock.filter(s => parseFloat(s.quantity_in_stock) <= parseFloat(s.reorder_level)).length; },
    expiringCount()    { return this.stock.filter(s => this.daysToExpiry(s.expiry_date) !== null && this.daysToExpiry(s.expiry_date) <= 90 && this.daysToExpiry(s.expiry_date) >= 0).length; },
    outOfStockCount()  { return this.stock.filter(s => parseFloat(s.quantity_in_stock) <= 0).length; },
    pendingRxCount()   { return this.prescriptions.filter(r => r.status === 'pending').length; },
    partialRxCount()   { return this.prescriptions.filter(r => r.status === 'partially_dispensed').length; },

    filteredStock() {
      var list = this.stock;
      var q = this.stockSearch.trim().toLowerCase();
      if (q) {
        list = list.filter(function(s) {
          return ((s.drug_name || "") + " " + (s.generic_name || "") + " " + (s.batch_number || "") + " " + (s.category || "")).toLowerCase().indexOf(q) >= 0;
        });
      }
      switch (this.stockFilter) {
        case 'low':
          list = list.filter(function(s){ return parseFloat(s.quantity_in_stock) <= parseFloat(s.reorder_level); });
          break;
        case 'expiring':
          var self = this;
          list = list.filter(function(s){ var d = self.daysToExpiry(s.expiry_date); return d !== null && d <= 90 && d >= 0; });
          break;
        case 'out':
          list = list.filter(function(s){ return parseFloat(s.quantity_in_stock) <= 0; });
          break;
        case 'expired':
          var self2 = this;
          list = list.filter(function(s){ return self2.isExpired(s); });
          break;
        case 'controlled':
          list = list.filter(function(s){ return s.is_controlled == 1; });
          break;
      }
      return list;
    },
    filteredRx() {
      var list = this.prescriptions;
      if (this.rxFilter) {
        list = list.filter(r => r.status === this.rxFilter);
      }
      var q = this.rxSearch.trim().toLowerCase();
      if (q) {
        list = list.filter(function(r) {
          return ((r.patient_name || "") + " " + (r.prescription_number || "") + " " + (r.patient_no || "")).toLowerCase().indexOf(q) >= 0;
        });
      }
      return list;
    },
    groupedRx() {
      var map = {}, order = [];
      this.filteredRx.forEach(function(rx) {
        var day = String(rx.prescription_date || "").slice(0, 10);
        var key = (rx.patient_id || rx.patient_name || "walkin") + "|" + day;
        if (!map[key]) {
          map[key] = { key: key, day: day, patient_name: rx.patient_name, patient_no: rx.patient_no, rxs: [] };
          order.push(key);
        }
        map[key].rxs.push(rx);
      });
      return order.map(function(k) { return map[k]; });
    },
    groupedQueue() {
      var map = {}, order = [];
      this.drugQueue.forEach(function(li) {
        var k = li.prescription_id;
        if (!map[k]) {
          map[k] = {
            prescription_id: k,
            patient_name: ((li.first_name || "") + " " + (li.last_name || "")).trim(),
            patient_no: li.patient_no,
            prescription_number: li.prescription_number,
            doctor_name: li.doctor_name,
            items: []
          };
          order.push(map[k]);
        }
        map[k].items.push(li);
      });
      return order;
    },
    canDispense() {
      return this.rxItems.some(function(line) {
        return line.dispense_now > 0 && line.stock_id;
      });
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) {
      this.$router.push("/login");
      return;
    }
    this.applyTheme();
    this.loadAll();
  },
  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    toggleRxGroup(key) {
      this.expandedRxGroups[key] = !this.expandedRxGroups[key];
    },
    groupDoctors(g) {
      var seen = {}, names = [];
      g.rxs.forEach(function(rx) {
        var n = rx.doctor_name || "—";
        if (!seen[n]) { seen[n] = 1; names.push(n); }
      });
      return names.join(", ");
    },
    groupStatusCounts(g) {
      var counts = {}, order = [];
      g.rxs.forEach(function(rx) {
        if (!counts[rx.status]) { counts[rx.status] = 0; order.push(rx.status); }
        counts[rx.status]++;
      });
      return order.map(function(s) { return { status: s, count: counts[s] }; });
    },
    t(key, fallback) {
      return (this.i18n && this.i18n.state.strings[key]) || fallback || key;
    },
    async loadDrugQueue() {
      this.loadingQueue = true;
      try {
        const r = await executeJson({ function: "getPharmacyQueueLineItems" }, hms);
        if (r.data && r.data.success) {
          this.drugQueue = r.data.data.PharmacyQueue || [];
        }
      } catch (e) { console.error(e); }
      this.loadingQueue = false;
    },
    openRxFromQueue(rxId) {
      this.tab = "prescriptions";
      var rx = this.prescriptions.find(function(r){ return r.prescription_id == rxId; });
      this.openDispense(rx || { prescription_id: rxId });
    },
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    printRx(id) { window.open(this.$router.resolve({ name: 'print', params: { type: 'rx', id }, query: { auto: '1' } }).href, '_blank'); },
    emptyStockForm() {
      return {
        stock_id: null,
        drug_name: "", generic_name: "", category: "", dosage_form: "", strength: "", unit: "tabs",
        quantity_in_stock: 0, reorder_level: 50, unit_cost: 0, unit_price: 0,
        expiry_date: "", batch_number: "", supplier: "", receiving_store: "",
        is_controlled: 0, is_narcotic: 0,
        status: "active"
      };
    },
    formatQty(v) {
      if (v === null || v === undefined || v === "") return "0";
      var n = Number(v);
      return n % 1 === 0 ? n.toString() : n.toFixed(2);
    },
    formatMoney(v) {
      if (v === null || v === undefined || v === "") return "0.00";
      return Number(v).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(d) {
      if (!d) return "—";
      try { var dt = new Date(d); if (isNaN(dt.getTime())) return d; return dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); } catch(e) { return d; }
    },
    formatDateTime(d) {
      if (!d) return "—";
      try { var dt = new Date(d); if (isNaN(dt.getTime())) return d; return dt.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch(e) { return d; }
    },
    daysToExpiry(d) {
      if (!d) return null;
      var dt = new Date(d);
      if (isNaN(dt.getTime())) return null;
      var ms = dt.getTime() - Date.now();
      return Math.floor(ms / 86400000);
    },
    expiryClass(d) {
      var days = this.daysToExpiry(d);
      if (days === null) return "expiry-ok";
      if (days < 0) return "expiry-critical";
      if (days <= 30) return "expiry-critical";
      if (days <= 90) return "expiry-soon";
      return "expiry-ok";
    },
    stockNumClass(s) {
      var qty = parseFloat(s.quantity_in_stock);
      var ro  = parseFloat(s.reorder_level);
      if (qty <= 0) return "stock-out";
      if (qty <= ro) return "stock-low";
      return "stock-ok";
    },
    isExpired(s) {
      if (s.status === 'expired') return true;
      var d = this.daysToExpiry(s.expiry_date);
      return d !== null && d < 0;
    },
    levelLabel(s) {
      var qty = parseFloat(s.quantity_in_stock);
      var ro  = parseFloat(s.reorder_level) || 0;
      if (qty <= 0)              return "Out";
      if (ro > 0 && qty <= ro)   return "Low";
      if (ro > 0 && qty >= ro*3) return "High";
      return "OK";
    },
    levelBadge(s) {
      var l = this.levelLabel(s);
      if (l === "Out")  return "hms-badge-danger";
      if (l === "Low")  return "hms-badge-warning";
      if (l === "High") return "hms-badge-info";
      return "hms-badge-success";
    },
    expiryStatusLabel(s) {
      if (this.isExpired(s))       return "Expired";
      if (s.status === 'recalled') return "Recalled";
      return "Ok";
    },
    expiryStatusBadge(s) {
      return (this.isExpired(s) || s.status === 'recalled') ? "hms-badge-danger" : "hms-badge-success";
    },
    rxStatusBadge(s) {
      switch (s) {
        case 'pending':              return "hms-badge-warning";
        case 'partially_dispensed':  return "hms-badge-info";
        case 'fully_dispensed':      return "hms-badge-success";
        case 'cancelled':            return "hms-badge-danger";
        default:                     return "hms-badge-neutral";
      }
    },
    rxStatusLabel(s) {
      var map = { pending:'Pending', partially_dispensed:'Partial', fully_dispensed:'Dispensed', cancelled:'Cancelled' };
      return map[s] || s;
    },
    rxItemBadge(s) {
      switch (s) {
        case 'pending':               return "hms-badge-warning";
        case 'partially_dispensed':   return "hms-badge-info";
        case 'dispensed':             return "hms-badge-success";
        case 'cancelled':             return "hms-badge-danger";
        default:                      return "hms-badge-neutral";
      }
    },
    rxItemLabel(s) {
      var map = { pending:'Pending', partially_dispensed:'Partial', dispensed:'Dispensed', cancelled:'Cancelled' };
      return map[s] || s;
    },
    stockAvailClass(line) {
      if (line.stock_available === null || line.stock_available === undefined) return "hms-muted-sm";
      var avail = parseFloat(line.stock_available);
      var need  = parseFloat(line.quantity_prescribed) - parseFloat(line.quantity_dispensed || 0);
      if (avail < need) return "stock-low";
      return "hms-text fw-semibold";
    },
    maxDispense(line) {
      var remaining = parseFloat(line.quantity_prescribed) - parseFloat(line.quantity_dispensed || 0);
      if (line.stock_available !== null && line.stock_available !== undefined) {
        return Math.min(remaining, parseFloat(line.stock_available));
      }
      return remaining;
    },
    stockSuggestions(line) {
      var name = (line.drug_name || "").toLowerCase();
      var self = this;
      return this.stock.filter(function(s) {
        return parseFloat(s.quantity_in_stock) > 0 &&
               s.status === 'active' && !self.isExpired(s) &&
               (((s.drug_name || "").toLowerCase().indexOf(name.split(' ')[0]) >= 0) ||
                ((s.generic_name || "").toLowerCase().indexOf(name.split(' ')[0]) >= 0));
      }).slice(0, 8);
    },
    onStockPicked(line) {
      var s = this.stock.find(x => x.stock_id == line.stock_id);
      if (s) {
        line.stock_available = s.quantity_in_stock;
        line.unit_price      = s.unit_price;
      }
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(function(){ self.toastMsg = ""; }, 2800);
    },
    setStockFilter(f) {
      this.tab = "stock";
      this.stockFilter = f;
    },
    goRxTab(filter) {
      this.tab = "prescriptions";
      this.rxFilter = filter;
    },

    async loadAll() {
      this.loading = true;
      try {
        await Promise.all([this.loadStock(), this.loadPrescriptions(), this.loadSuppliers(), this.loadHospStores()]);
      } catch (e) { console.error(e); }
      this.loading = false;
    },
    async loadSuppliers() {
      try {
        var r = await executeJson({ function: "getStoresSuppliers" }, hms);
        if (r.data && r.data.success) this.suppliers = (r.data.data && r.data.data.Suppliers) || [];
      } catch (e) { console.error(e); }
    },
    async loadHospStores() {
      try {
        var r = await executeJson({ function: "getHospitalStores" }, hms);
        if (r.data && r.data.success) this.hospStores = (r.data.data && r.data.data.Stores) || [];
      } catch (e) { console.error(e); }
    },
    async loadStock() {
      try {
        var r = await executeJson({ function: "getPharmacyStock", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.stock = (r.data.data && r.data.data.Stock) || [];
      } catch (e) { console.error(e); }
    },
    async loadPrescriptions() {
      try {
        var r = await executeJson({ function: "getPrescriptions" }, hms);
        if (r.data && r.data.success) this.prescriptions = (r.data.data && r.data.data.Prescriptions) || [];
      } catch (e) { console.error(e); }
    },

    // ===== STOCK =====
    openAddStock() {
      this.editingStock = false;
      this.batchMode    = false;
      this.stockForm    = this.emptyStockForm();
      this.stockError   = "";
      this.showStockForm = true;
    },
    openAddBatch(s) {
      // New batch of an existing drug = new stock row with drug identity prefilled
      this.editingStock = false;
      this.batchMode    = true;
      this.stockForm = Object.assign(this.emptyStockForm(), {
        drug_name:     s.drug_name || "",
        generic_name:  s.generic_name || "",
        category:      s.category || "",
        dosage_form:   s.dosage_form || "",
        strength:      s.strength || "",
        unit:          s.unit || "tabs",
        reorder_level: Number(s.reorder_level) || 0,
        unit_cost:     Number(s.unit_cost) || 0,
        unit_price:    Number(s.unit_price) || 0,
        supplier:        s.supplier || "",
        receiving_store: s.receiving_store || "",
        is_controlled:   Number(s.is_controlled) || 0,
        is_narcotic:     Number(s.is_narcotic) || 0
      });
      this.stockError = "";
      this.showStockForm = true;
    },
    openEditStock(s) {
      this.editingStock = true;
      this.batchMode    = false;
      this.stockForm = Object.assign(this.emptyStockForm(), {
        stock_id:           s.stock_id,
        drug_name:          s.drug_name || "",
        generic_name:       s.generic_name || "",
        category:           s.category || "",
        dosage_form:        s.dosage_form || "",
        strength:           s.strength || "",
        unit:               s.unit || "",
        reorder_level:      Number(s.reorder_level) || 0,
        unit_cost:          Number(s.unit_cost) || 0,
        unit_price:         Number(s.unit_price) || 0,
        expiry_date:        s.expiry_date ? String(s.expiry_date).slice(0,10) : "",
        batch_number:       s.batch_number || "",
        supplier:           s.supplier || "",
        receiving_store:    s.receiving_store || "",
        is_controlled:      Number(s.is_controlled) || 0,
        is_narcotic:        Number(s.is_narcotic) || 0,
        status:             s.status || "active"
      });
      this.stockError = "";
      this.showStockForm = true;
    },
    async saveStock() {
      this.stockError = "";
      if (!this.stockForm.drug_name) { this.stockError = "Drug name is required."; return; }
      this.processing = true;
      try {
        var fn = this.editingStock ? "updateStock" : "addStock";
        var r  = await executeJson(Object.assign({ function: fn }, this.stockForm), hms);
        if (r.data && r.data.success) {
          this.showToast(this.editingStock ? "Stock updated" : "Stock added");
          this.showStockForm = false;
          await this.loadStock();
        } else { this.stockError = r.data.message || "Failed."; }
      } catch (e) { this.stockError = "Connection error."; }
      this.processing = false;
    },

    async removeStockRow(s) {
      if (!confirm('Remove empty row "' + s.drug_name + (s.batch_number ? ' (batch ' + s.batch_number + ')' : '') + '"? This just hides it — historical movements stay for audit.')) return;
      try {
        const r = await executeJson({ function: "deletePharmacyStock", stock_id: s.stock_id }, hms);
        if (r.data && r.data.success) await this.loadStock();
        else alert((r.data && r.data.message) || "Failed to remove row.");
      } catch (e) { console.error(e); }
    },

    // ===== STOCK ADJUSTMENTS =====
    openAdjust(s) {
      this.adjustForm = {
        stock_id: s.stock_id, drug_name: s.drug_name, unit: s.unit || "",
        batch_number: s.batch_number || "",
        current_qty: Number(s.quantity_in_stock) || 0,
        new_quantity: Number(s.quantity_in_stock) || 0,
        reason: "", notes: ""
      };
      this.adjustError = "";
      this.showAdjust  = true;
    },
    async saveAdjust() {
      this.adjustError = "";
      var f = this.adjustForm;
      if (f.new_quantity === "" || f.new_quantity === null || isNaN(Number(f.new_quantity))) { this.adjustError = "New quantity is required."; return; }
      if (Number(f.new_quantity) < 0) { this.adjustError = "Quantity cannot be negative."; return; }
      if (!f.reason) { this.adjustError = "Adjustment reason is required."; return; }
      if (Number(f.new_quantity) === Number(f.current_qty)) { this.adjustError = "New quantity equals current stock — nothing to adjust."; return; }
      this.adjustBusy = true;
      try {
        var r = await executeJson({ function: "adjustStock", stock_id: f.stock_id, new_quantity: f.new_quantity, reason: f.reason, notes: f.notes }, hms);
        if (r.data && r.data.success) {
          this.showToast("Stock adjusted");
          this.showAdjust = false;
          await this.loadStock();
        } else { this.adjustError = (r.data && r.data.message) || "Failed."; }
      } catch (e) { this.adjustError = "Connection error."; }
      this.adjustBusy = false;
    },

    // ===== LOW-STOCK ALERT RULES =====
    async openAlerts() {
      this.showAlerts = true;
      this.alertError = "";
      await this.loadAlertRules();
    },
    async loadAlertRules() {
      try {
        var r = await executeJson({ function: "listPharmacyAlertRules" }, hms);
        if (r.data && r.data.success) this.alertRules = (r.data.data && r.data.data.rules) || [];
      } catch (e) { console.error(e); }
    },
    async addAlertRule() {
      this.alertError = "";
      if (!this.alertEmail.trim()) { this.alertError = "Email is required."; return; }
      this.alertBusy = true;
      try {
        var r = await executeJson({ function: "savePharmacyAlertRule", email_to: this.alertEmail.trim(), alert_type: "low_stock", cooldown_hours: this.alertCooldown || 24 }, hms);
        if (r.data && r.data.success) {
          this.alertEmail = "";
          this.showToast("Alert recipient added");
          await this.loadAlertRules();
        } else { this.alertError = (r.data && r.data.message) || "Failed."; }
      } catch (e) { this.alertError = "Connection error."; }
      this.alertBusy = false;
    },
    async removeAlertRule(rule) {
      try {
        var r = await executeJson({ function: "deletePharmacyAlertRule", rule_id: rule.rule_id }, hms);
        if (r.data && r.data.success) {
          this.showToast("Recipient removed");
          await this.loadAlertRules();
        } else { this.showToast((r.data && r.data.message) || "Failed", true); }
      } catch (e) { this.showToast("Connection error", true); }
    },

    // ===== DISPENSE =====
    async openDispense(rx) {
      this.dispenseError = "";
      this.rxDetail = null;
      this.rxItems = [];
      this.showDispense = true;
      this.dispenseLoading = true;
      try {
        var r = await executeJson({ function: "getPrescriptionDetail", prescription_id: rx.prescription_id }, hms);
        if (r.data && r.data.success) {
          this.rxDetail = r.data.data.Prescription;
          this.rxItems = (r.data.data.Items || []).map(function(line) {
            var remaining = parseFloat(line.quantity_prescribed) - parseFloat(line.quantity_dispensed || 0);
            line.dispense_now = (line.status === 'dispensed' || line.status === 'cancelled') ? 0 : remaining;
            return line;
          });
        } else {
          this.dispenseError = r.data.message || "Failed to load.";
        }
      } catch (e) { this.dispenseError = "Connection error."; }
      this.dispenseLoading = false;
    },
    closeDispense() {
      this.showDispense = false;
      this.rxDetail = null;
      this.rxItems = [];
    },
    async doDispense() {
      this.dispenseError = "";
      if (!this.canDispense) { this.dispenseError = "Set a non-zero dispense quantity on at least one line with stock matched."; return; }

      var items = this.rxItems
        .filter(line => line.dispense_now > 0 && line.stock_id && line.status !== 'dispensed')
        .map(line => ({
          item_id: line.item_id,
          stock_id: line.stock_id,
          quantity_dispensed: parseFloat(line.dispense_now)
        }));

      this.processing = true;
      try {
        var r = await executeJson({ function: "dispensePrescription", prescription_id: this.rxDetail.prescription_id, items: items }, hms);
        if (r.data && r.data.success) {
          this.showToast("Prescription dispensed");
          // If pharmacist selected a "Save & queue to ___" destination, route the patient now.
          var pid = this.rxDetail && (this.rxDetail.patient_id || this.rxDetail.patientId);
          if (this.$refs.sqDispense && pid) {
            var q = await this.$refs.sqDispense.queueIfSelected(pid);
            if (q && q.ok) this.showToast("Queued to " + q.dept_name + " (ticket " + q.ticket_number + ")");
          }
          this.closeDispense();
          await Promise.all([this.loadStock(), this.loadPrescriptions()]);
        } else { this.dispenseError = r.data.message || "Failed."; }
      } catch (e) { this.dispenseError = "Connection error."; }
      this.processing = false;
    },

    // ===== VIEW =====
    async openViewRx(rx) {
      this.rxDetail = null;
      this.rxItems = [];
      this.showViewRx = true;
      try {
        var r = await executeJson({ function: "getPrescriptionDetail", prescription_id: rx.prescription_id }, hms);
        if (r.data && r.data.success) {
          this.rxDetail = r.data.data.Prescription;
          this.rxItems  = r.data.data.Items || [];
        }
      } catch (e) { /* keep empty */ }
    },

    // ===== CANCEL =====
    confirmCancel(rx) { this.cancelingRx = rx; },
    async doCancel() {
      if (!this.cancelingRx) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "cancelPrescription", prescription_id: this.cancelingRx.prescription_id }, hms);
        if (r.data && r.data.success) {
          this.showToast("Prescription cancelled");
          this.cancelingRx = null;
          await this.loadPrescriptions();
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch (e) { this.showToast("Connection error", true); }
      this.processing = false;
    }
  }
};
</script>
